import React, { useEffect, useRef, useState } from "react";
import "./LandingPage.css"; // Custom CSS file for styling
import { FaBars, FaTimes } from "react-icons/fa"; // Menu icons from react-icons
import { Link } from "react-router-dom";

interface SectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "What is VoinoxAI?",
    answer:
      "VoinoxAI is an advanced app that transcribes audio into text and generates summaries for meetings, lectures, and more.",
  },
  {
    question: "How does the transcription feature work?",
    answer:
      "With VoinoxAI, you can record audio directly in the app, upload an audio file, or provide a YouTube link. The app will transcribe and summarize it for you quickly and accurately.",
  },
  {
    question: "What platforms is VoinoxAI available on?",
    answer: "The app is available for iOS and Android devices.",
  },
  {
    question: "How much does it cost?",
    answer: "VoinoxAI offers a subscription plan for just $5.99/month.",
  },
  {
    question: "Is my audio recording private?",
    answer:
      "Yes, your audio recordings are completely private. VoinoxAI uses end-to-end encryption to ensure your data is secure. Audio recordings are stored in a secure location and are never shared without your consent.",
  },
];

const reviews = [
  {
    text: "VoinoxAI transformed my productivity. Summarizing meetings has never been easier!",
    author: "John D.",
  },
  {
    text: "I use VoinoxAI every day. It's like having a personal assistant for all my notes.",
    author: "Sarah L.",
  },
  {
    text: "Absolutely love this app! It captures every detail perfectly. Highly recommended.",
    author: "David R.",
  },
  {
    text: "As a student, this app has been a lifesaver for transcribing lectures. Amazing!",
    author: "Emily W.",
  },
  {
    text: "Five stars! The chatbot feature makes accessing summaries a breeze.",
    author: "Michael K.",
  },
  {
    text: "A must-have for professionals. Summaries are accurate and concise. Brilliant app!",
    author: "Jessica H.",
  },
  {
    text: "I've saved hours of work with VoinoxAI. The transcription is flawless.",
    author: "Chris T.",
  },
  {
    text: "This app is revolutionary. Perfect for keeping track of meetings and projects.",
    author: "Amanda G.",
  },
  {
    text: "The best transcription tool I've ever used. Worth every penny!",
    author: "Daniel P.",
  },
  {
    text: "Super intuitive and reliable. My productivity has skyrocketed thanks to VoinoxAI.",
    author: "Laura B.",
  },
];

const Accordion: React.FC<{ question: string; answer: string }> = ({
  question,
  answer,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleAccordion = () => {
    setIsOpen((prev) => !prev);

    if (contentRef.current) {
      if (!isOpen) {
        contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
      } else {
        contentRef.current.style.height = "0";
      }
    }
  };

  return (
    <div className="accordion-item">
      <div className="accordion-header" onClick={toggleAccordion}>
        <h3>{question}</h3>
        <span>{isOpen ? "-" : "+"}</span>
      </div>
      <div
        className="accordion-body"
        ref={contentRef}
        style={{
          overflow: "hidden",
          height: "0",
          transition: "height 0.3s ease",
        }}
      >
        <div className="accordion-content">{answer}</div>
      </div>
    </div>
  );
};

const Section: React.FC<SectionProps> = ({ id, title, children }) => (
  <section id={id} className="full-screen-section">
    <div className={`section-content ${id === "reviews" ? "no-padding" : ""}`}>
      <h2>{title}</h2>
      {children}
    </div>
  </section>
);

const LandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll(".full-screen-section");
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        const delay = index * 200; // Delay animations for each section

        if (rect.top < window.innerHeight && rect.bottom > 0) {
          // Add visible and shown classes
          if (!section.classList.contains("shown")) {
            setTimeout(() => {
              section.classList.add("visible", "shown");
            }, delay);
          }
        }
      });
    };

    const reviewsContainer = document.querySelector(
      ".reviews-container"
    ) as HTMLElement;
    let scrollAmount = 0;

    const autoScrollReviews = () => {
      if (reviewsContainer) {
        scrollAmount += 1;
        if (
          scrollAmount >=
          reviewsContainer.scrollWidth - reviewsContainer.clientWidth
        ) {
          scrollAmount = 0; // Reset scroll
        }
        reviewsContainer.scrollLeft = scrollAmount;
      }
    };

    const interval = setInterval(autoScrollReviews, 30); // Auto-scroll speed

    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearInterval(interval); // Cleanup interval on unmount
    };
  }, []);

  const scrollToSection = (sectionId: string): void => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="landing-page">
      {/* Navbar */}
      <nav className={`navbar ${isMenuOpen ? "open" : ""}`}>
        <img
          onClick={() => scrollToSection("home")}
          src={require("../../assets/images/app_logo.png")} /* Update the path to your logo */
          alt="App Logo"
          className="app-logo"
        />
        <button
          className="menu-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
        <ul className={`nav-links ${isMenuOpen ? "visible" : ""}`}>
          <li onClick={() => scrollToSection("features")}>Features</li>
          <li onClick={() => scrollToSection("reviews")}>Reviews</li>
          {/* <li onClick={() => scrollToSection("how-it-works")}>How It Works</li> */}
          <li onClick={() => scrollToSection("pricing")}>Pricing</li>
          <li onClick={() => scrollToSection("faqs")}>FAQs</li>
        </ul>
      </nav>

      {/* Header Section */}
      <header id="home" className="header full-screen-section always-visible">
        <div className="header-content">
          <div className="text-content">
            <h1 className="app-name">Voinox AI</h1>
            <p className="tagline">
              Effortlessly Transcribe and Summarize Audio
            </p>
            <button className="download-button">Download the App</button>
          </div>
          <div className="image-content">
            <img
              src={require("../../assets/images/iphone_mockup.png")}
              alt="iPhone Mockup"
              className="iphone-mockup"
            />
          </div>
        </div>
      </header>

      {/* Features Section */}
      <Section id="features" title="What is VoinoxAI?">
        <p>
          VoinoxAI allows you to transcribe audio into text and generate
          detailed summaries effortlessly. Whether it's a meeting, an audio
          upload, or a YouTube link, we’ve got you covered.
        </p>
        <div className="mockups-container">
          <img
            src={require("../../assets/images/recording_mockup.png")}
            alt="Mockup 1"
            className="mockup-image"
          />
          <img
            src={require("../../assets/images/upload_mockup.png")}
            alt="Mockup 2"
            className="mockup-image"
          />
          <img
            src={require("../../assets/images/youtube_mockup.png")}
            alt="Mockup 3"
            className="mockup-image"
          />
          <img
            src={require("../../assets/images/summary_mockup.png")}
            alt="Mockup 3"
            className="mockup-image"
          />
        </div>
      </Section>

      {/* Reviews Section */}
      <Section id="reviews" title="What Our Users Say">
        <div className="reviews-container">
          {reviews.map((review, index) => (
            <div key={index} className="review-card">
              <p className="review-text">"{review.text}"</p>
              <div className="stars">
                {Array.from({ length: 5 }).map((_, starIndex) => (
                  <span key={starIndex} className="star">
                    ★
                  </span>
                ))}
              </div>
              <p className="review-author">- {review.author}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Pricing Section */}
      {/* Pricing Section */}
      <Section id="pricing" title="Affordable Pricing">
        <p>
          Enjoy all the powerful features of VoinoxAI for just{" "}
          <strong>$5.99/month</strong>. No hidden charges, cancel anytime.
        </p>
      </Section>

      {/* FAQs Section */}
      <Section id="faqs" title="Frequently Asked Questions">
        <div className="accordion-container">
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              question={faq.question}
              answer={faq.answer}
            />
          ))}
        </div>
      </Section>

      {/* Footer */}
      <footer className="footer">
        <p>© 2025 VoinoxAI. All rights reserved.</p>
        <div className="footer-links">
          <Link to="/privacy-policy">Privacy Policy</Link> |
          <Link to="/terms-of-use">Terms of Use</Link> |
          <Link to="/contact-us">Contact Us</Link>
        </div>
        <button className="download-button">Download the App</button>
      </footer>
    </div>
  );
};

export default LandingPage;
